import React from 'react';

import Typography from '@mui/material/Typography';

import HandsonArticleLayout from './components/HandsonArticleLayout';
import WorkflowChatBubble from './components/WorkflowChatBubble';
import WorkflowChecklist from './components/WorkflowChecklist';
import WorkflowCodeBlock from './components/WorkflowCodeBlock';
import WorkflowDirectoryTree from './components/WorkflowDirectoryTree';
import WorkflowGoalBox from './components/WorkflowGoalBox';
import WorkflowInfoBox from './components/WorkflowInfoBox';
import WorkflowLinkCard from './components/WorkflowLinkCard';
import WorkflowList from './components/WorkflowList';
import WorkflowLockedCodeBlock from './components/WorkflowLockedCodeBlock';
import WorkflowPracticeBox from './components/WorkflowPracticeBox';
import WorkflowProcedureFlow from './components/WorkflowProcedureFlow';
import WorkflowSection from './components/WorkflowSection';
import WorkflowTable from './components/WorkflowTable';
import WorkflowTableOfContents from './components/WorkflowTableOfContents';
import WorkflowText from './components/WorkflowText';
import WorkflowTipBox from './components/WorkflowTipBox';

const seriesSteps = [
  {
    label: 'structure/tech',
    title: 'structure/techルールの作成',
    path: '/devdocs/handson/structure-tech',
  },
  {
    label: 'スキーマ+API仕様書',
    title: 'スキーマ定義とAPI仕様書の生成',
    path: '/devdocs/handson/schema-api',
  },
  {
    label: 'API実装ワークフロー',
    title: 'API実装ワークフローを作成する',
    path: '/devdocs/handson/api-workflow',
  },
  { label: 'E2Eテスト', title: 'E2Eテストを実行する', active: true },
];

const HandsonE2e: React.FC = () => {
  return (
    <HandsonArticleLayout
      title="実装編 — E2Eテスト"
      seriesName="実装編"
      tags={['Hands-on', 'E2E', 'Test', 'Integration']}
      seriesSteps={seriesSteps}
      prevLink={{ label: 'API実装ワークフロー', path: '/devdocs/handson/api-workflow' }}
      nextLink={{
        label: 'プロジェクト作成 + Steeringルール設置',
        path: '/devdocs/handson/project-setup',
      }}
    >
      <WorkflowGoalBox
        prerequisites={[
          '「実装編 — API実装ワークフロー」が完了している',
          'Docker環境（MySQL + Flyway）が動作する',
        ]}
        completionCriteria={[
          '.kiro/steering/create_e2e_testcase.md が作成されている',
          '.kiro/e2e_test/{テストケース名}/{テストケース名}.e2e.md が生成されている',
          'E2Eテストが全ケースOKで、結果が result_{yyyyMMddHHmmss}.md に記録されている',
          'usecases.md の全ユースケースが少なくとも1つのテストケースでカバーされている',
        ]}
      >
        E2Eテストケース生成ルール（create_e2e_testcase.md）を作成し、全APIを組み合わせた業務シナリオテストを実行して全件OKを達成する。
      </WorkflowGoalBox>

      <WorkflowLinkCard
        title="実装編 — API実装ワークフロー"
        description="前の記事"
        path="/devdocs/handson/api-workflow"
      />

      <WorkflowTableOfContents
        items={[
          { id: 'section-1', label: 'この章で体験すること' },
          { id: 'section-2', label: 'E2Eテストの設計方針' },
          { id: 'section-3', label: 'create_e2e_testcase.md を作成する' },
          { id: 'section-4', label: 'E2Eテストケースを生成する' },
          { id: 'section-5', label: 'テストを実行する' },
          { id: 'section-6', label: 'バグ検知時の対応' },
          { id: 'section-7', label: '現在のプロジェクト構成' },
          { id: 'section-8', label: 'ベストプラクティスとアンチパターン' },
          { id: 'section-9', label: 'この章で学んだこと' },
          { id: 'section-10', label: 'バックエンド実装編の全体まとめ' },
        ]}
      />

      {/* セクション1 */}
      <WorkflowSection title="この章で体験すること" id="section-1" number={1}>
        <WorkflowSection title="単体テストの次のステップ — 業務シナリオの検証" subNumber="1.1">
          <WorkflowText>
            「実装編 —
            API実装ワークフロー」で各APIの単体テストは完了しました。「個々のAPIが仕様通りに動く」ことは確認済みです。しかし、実際のユーザーは複数のAPIを組み合わせて業務を遂行します。
          </WorkflowText>

          <WorkflowLinkCard
            title="実装編 — API実装ワークフロー"
            description="単体テストの実行手順"
            path="/devdocs/handson/api-workflow"
          />

          <WorkflowProcedureFlow
            steps={[
              {
                label: '単体テスト（前記事で完了）',
                sub: '1API単位の検証 / 正常系・異常系',
                variant: 'external',
              },
              { label: 'E2Eテスト（この章）', sub: '業務シナリオの検証 / ステータス遷移の整合性' },
            ]}
          />

          <WorkflowTipBox title="なぜ単体テストだけでは不十分なのか">
            <WorkflowText>
              単体テストでは各APIが個別に正しく動くことを確認しています。しかし、複数APIを組み合わせた時に初めて顕在化する問題があります:
            </WorkflowText>
            <WorkflowList
              items={[
                'データ連携の不整合: TODO登録APIで返却されたIDを、更新APIに渡した時にフォーマットが合わない',
                'ステータス遷移の矛盾: 「完了」にしたTODOを再度「未着手」に戻せてしまう（仕様では不可のはず）',
                '一覧表示の漏れ: 登録したTODOが一覧取得で返ってこない（検索条件の不備）',
                '削除後の参照: 削除済みTODOを取得した時の挙動が未定義',
              ]}
            />
            <WorkflowText>
              これらは「木を見る」テスト（単体）では見つからず、「森を見る」テスト（E2E）で初めて検出されます。
            </WorkflowText>
          </WorkflowTipBox>
        </WorkflowSection>

        <WorkflowSection title="この章の流れ" subNumber="1.2">
          <WorkflowList
            variant="ordered"
            items={[
              'E2Eテストと単体テストの違いを理解する',
              'create_e2e_testcase.md を作成する',
              'E2Eテストケースを生成する',
              'テストを実行する',
              'バグ検知時の対応を理解する',
            ]}
          />
        </WorkflowSection>

        <WorkflowSection title="参照する記事" subNumber="1.3">
          <WorkflowLinkCard
            title="バックエンド開発編 Part6 — E2Eテスト"
            description="E2Eテストの設計方針・create_e2e_testcase.md全文・実体験レポート"
            path="/devdocs/workflow/be-step6"
          />
        </WorkflowSection>
      </WorkflowSection>

      {/* セクション2 */}
      <WorkflowSection title="E2Eテストの設計方針" id="section-2" number={2}>
        <WorkflowSection title="単体テストとの棲み分け" subNumber="2.1">
          <WorkflowInfoBox type="important">
            <Typography variant="body2" fontWeight={600}>
              E2Eテストに含めないこと:
            </Typography>
            <WorkflowList
              items={[
                'バリデーションの全パターン網羅（→ 単体テストで実施済み）',
                '個別のエラーケース確認（→ 単体テストで実施済み）',
                '1APIだけで完結するテスト（→ それは単体テスト）',
              ]}
            />
            <Typography variant="body2" fontWeight={600} sx={{ mt: 1 }}>
              E2Eテストに含めること:
            </Typography>
            <WorkflowList
              items={[
                '複数APIを組み合わせた業務フロー（登録→参照→更新→削除）',
                'データのライフサイクル全体の検証',
                'ユースケースの全量カバレッジ確認',
              ]}
            />
          </WorkflowInfoBox>
        </WorkflowSection>

        <WorkflowSection title="テストケースの設計観点" subNumber="2.2">
          <WorkflowTable
            headers={['観点', '内容', 'TODOアプリでの例']}
            rows={[
              [
                '業務フロー',
                'ユーザーの操作順序を再現する',
                'TODO登録→一覧で確認→ステータス更新→完了確認',
              ],
              [
                'データライフサイクル',
                '登録→参照→更新→削除の全工程',
                'TODOを作成してから削除するまでの全操作',
              ],
              ['ステータス遷移', '状態変化の全パターン', '未着手→進行中→完了、未着手→完了（直接）'],
              ['データパターン', '件数や条件の境界値', 'TODO 0件の一覧取得、複数件の一覧取得'],
            ]}
          />
        </WorkflowSection>
      </WorkflowSection>

      {/* セクション3 */}
      <WorkflowSection title="create_e2e_testcase.md を作成する" id="section-3" number={3}>
        <WorkflowSection title="ファイルの位置づけ" subNumber="3.1">
          <WorkflowTable
            headers={['項目', '内容']}
            rows={[
              ['ファイル', '.kiro/steering/create_e2e_testcase.md'],
              ['読み込みモード', 'manual（テストケース生成時に明示的に渡す）'],
              ['起動タイミング', '全APIの単体テスト完了後'],
            ]}
          />
        </WorkflowSection>

        <WorkflowSection title="書くべき情報" subNumber="3.2">
          <WorkflowTable
            headers={['セクション', '内容']}
            rows={[
              ['目的', 'E2Eテストケースを生成するルール'],
              ['入力', 'rfp.md を起点に関連ファイルを探索する'],
              ['出力', 'テストケースファイルの配置先と命名規則'],
              ['テストケース構造', 'テストケースのテンプレート'],
              ['設計ルール', 'シナリオ構成・データパターン・ユースケース網羅性'],
              ['テスト実行ルール', '結果ファイルの出力方法'],
              ['バグ検知時の対応', '期待値修正OK / 実装修正NG'],
              ['禁止事項', '単体テスト観点を含めない等'],
            ]}
          />
        </WorkflowSection>

        <WorkflowSection title="自分で書いてみる" subNumber="3.3">
          <WorkflowText>以下のファイルを作成してください。</WorkflowText>
          <WorkflowCodeBlock language="text" code=".kiro/steering/create_e2e_testcase.md" />

          <WorkflowInfoBox type="warning">
            <Typography variant="body2">
              <strong>注意</strong>: 紹介記事の create_e2e_testcase.md
              は複数rfp間のクロスドメイン観点を含む大規模なルールです。TODOアプリは単一ドメイン（TODO管理のみ）なので、以下を省略できます:
            </Typography>
            <WorkflowList
              items={[
                'クロスドメイン観点（rfp間のデータ依存）',
                'master_type の各区分パターン',
                '権限の違いによる操作可否（認証なし）',
              ]}
            />
            <Typography variant="body2" sx={{ mt: 1 }}>
              代わりに「業務フロー」「データライフサイクル」「ステータス遷移」に集中してください。
            </Typography>
          </WorkflowInfoBox>
        </WorkflowSection>

        <WorkflowSection title="create_e2e_testcase.md - 完成例" subNumber="3.4">
          <WorkflowLockedCodeBlock password="Zt5wDk">
            {`---
inclusion: manual
---

# E2Eテストケース生成ルール

## 目的

全APIの単体テスト完了後に、複数APIを組み合わせた業務シナリオテストのケースを生成する。

## 入力

- .kiro/requirements/**/*.rfp.md（ユーザーが手動で指定する）

## 出力

- .kiro/e2e_test/{テストケース名}/{テストケース名}.e2e.md

## 生成フロー

### 1. 関連ファイルの探索

1. 指定された *.rfp.md の機能名を特定する。
2. 関連する *.proposal.md を探索する。
3. 関連する *.usecases.md を探索する。
4. 関連する API仕様書（docs/api/）を確認する。

### 2. ユースケースの全量確認

1. 全ての *.usecases.md からUC一覧を抽出する。
2. 全UCがテストケースでカバーされることを確認する。

### 3. テストケースの設計

以下の観点でテストケースを設計する:

#### 業務フロー観点

- ユーザーの業務フローを再現する。
- データのライフサイクル（登録→参照→更新→削除）を網羅する。
- 複数のユースケースを組み合わせてシナリオを構成する。

#### データパターン観点

- ステータス遷移の全パターン。
- 件数の境界値（0件/1件/複数件）。
- 検索条件の組み合わせ。

#### エラーケース観点（業務フロー内のみ）

- 業務フロー内で発生しうるエラーからの復旧シナリオ。
- 不正なステータス遷移の確認。

## テストケースファイルの構造

# {テストケース名} E2Eテスト

## 対象機能

- {機能名}（{概要}）

## カバーするユースケース

| UC-ID | ユースケース名 | 対応ケースNo |
|-------|----------------|--------------|
| UC-XX01 | ... | 1, 3 |

## テストケース一覧

| No | ケース名 | 確認観点 | 概要 |
|----|----------|----------|------|
| 1  | ... | 業務フロー | ... |

## 前提条件

（テスト開始前の前提状態）

---

## ケース{No}: {ケース名}

### Step {N}: {ステップ名}

#### API呼び出し

(curl コマンド — 改行なし)

#### 期待値

(Response JSON + DB確認SQL + 期待値)

## テストケース設計ルール

### シナリオ構成

- シナリオは「前提データ準備 → 一連のAPI呼び出し → 最終状態の検証」で構成する。
- 前提データは極力API経由で登録する。API登録できないデータのみSQL直接投入を許可する。
- 各ステップに期待値（Response + DB状態）を記載する。
- curl コマンドは改行なしで記載する。

### ユースケースの網羅性

- 「カバーするユースケース」テーブルで全UCが少なくとも1つのケースに対応していること。
- 対応するケースがないUCがあれば追加ケースを作成する。

### ケース間のデータ再利用

- 前のケースで登録したデータを後続ケースで再利用してよい（毎回クリアしない）。
- ただし実行順序への依存を「前提条件」セクションに明記する。

## テスト実行ルール

### テスト結果の出力

- テスト実行結果は .kiro/e2e_test/{テストケース名}/result_{yyyyMMddHHmmss}.md に出力する。

### テスト環境

- local_test.md の手順に従ってDocker環境を起動する。
- テスト用DBをクリーン&マイグレーションしてからテストを開始する。

### バグ検知時の対応

テスト実行中にNGが発生した場合、以下の3分類で判断する。

| 分類 | 判断基準 | 対応 |
|------|---------|------|
| テストケース誤り | テストケースの期待値がAPI仕様書と合っていない。curlのパラメータが間違っている。前提データが不足している | .e2e.md を修正して再実行する |
| 実装バグ | API仕様書通りの期待値なのに実際のレスポンスが異なる。処理ロジックの誤り | 結果ファイルにNG内容を記録する。実装コードの修正は行わない |
| 設計バグ | API仕様書やスキーマ定義自体に問題がある。テーブル設計の不備 | 結果ファイルにNG内容を記録する。設計書・実装コードの修正は行わない |

- テストケース誤りの場合のみ .e2e.md を修正して再実行する。
- 実装バグ・設計バグの場合は結果をNGとして記録し、実装の修正は行わない。
- 実装修正・設計修正は結果レビュー後に人間が別途対応する。

## 対象外の観点

E2Eテストケースには以下を含めない:

- バリデーション（必須チェック、桁数チェック等）
- 単一APIの正常系/異常系の網羅
- 個別エラーケースの確認

## チェックリスト

テストケースを生成した後、以下を確認する:

- [ ] 全UCが「カバーするユースケース」テーブルに含まれているか
- [ ] 全UCが少なくとも1つのテストケースに対応しているか
- [ ] 各ケースに期待値（Response + DB状態）が記載されているか
- [ ] curl コマンドが改行なしで記載されているか
- [ ] 業務フローとして自然な順序で操作が記述されているか
- [ ] データライフサイクル（登録→参照→更新→削除）が網羅されているか

## 禁止事項

- usecases.md に存在しないUCをテスト対象に含めない。
- API経由で登録可能なデータをDB直接投入で作成しない。
- 単一APIの正常系/異常系のみで構成されたケースを作成しない。
- curl コマンドに改行を含めない。
- テスト実行中に実装バグ・設計バグを検知しても、実装コード・設計書を修正しない。結果をNGとして記録するのみとする。`}
          </WorkflowLockedCodeBlock>
        </WorkflowSection>
      </WorkflowSection>

      {/* セクション4 */}
      <WorkflowSection title="E2Eテストケースを生成する" id="section-4" number={4}>
        <WorkflowSection title="起動方法" subNumber="4.1">
          <WorkflowChatBubble role="human">
            {`#create_e2e_testcase.md 以下のrfpに対するE2Eテストケースを作成して
#todo.rfp.md`}
          </WorkflowChatBubble>
        </WorkflowSection>

        <WorkflowSection title="生成後の確認" subNumber="4.2">
          <WorkflowText>テストケースが生成されたら、以下を確認してください。</WorkflowText>
          <WorkflowChecklist
            groups={[
              {
                title: '生成後チェックリスト',
                items: [
                  'usecases.md の全UCがカバーされているか',
                  '業務フローが自然な順序か（登録→参照→更新→削除）',
                  '各ステップの期待値がAPI仕様書と整合しているか',
                  'curl コマンドが正しいか（Endpoint, Method, Body）',
                ],
              },
            ]}
          />

          <WorkflowInfoBox type="warning">
            <Typography variant="body2">
              <strong>注意</strong>:
              テストケースの期待値は完璧ではない可能性があります。AIはproposal/usecasesレベルの情報からテストケースを生成するため、詳細設計レベルの仕様（デフォルト値の挙動等）が反映されないことがあります。テスト実行中に期待値の不備が見つかった場合、AIがテストケースを修正して再実行します。
            </Typography>
          </WorkflowInfoBox>
        </WorkflowSection>
      </WorkflowSection>

      {/* セクション5 */}
      <WorkflowSection title="テストを実行する" id="section-5" number={5}>
        <WorkflowSection title="実行の指示" subNumber="5.1">
          <WorkflowText>テストケースを確認したら、実行を指示します。</WorkflowText>
          <WorkflowChatBubble role="human">
            {`#local_test.md #create_e2e_testcase.md E2Eテストを実行してください
#todo.e2e.md`}
          </WorkflowChatBubble>

          <WorkflowText>
            #local_test.md を含めることで、「実装編 —
            API実装ワークフロー」と同じDocker環境でテストが実行されます。Docker環境の起動・テスト用DBの準備・アプリケーション起動/停止はAIが
            local_test.md の手順に従って自動で実行します。
          </WorkflowText>

          <WorkflowLinkCard
            title="実装編 — API実装ワークフロー"
            description="Docker環境でのテスト実行手順"
            path="/devdocs/handson/api-workflow"
          />
        </WorkflowSection>

        <WorkflowSection title="結果の確認" subNumber="5.2">
          <WorkflowText>テスト結果ファイルの構造:</WorkflowText>
          <WorkflowCodeBlock
            language="text"
            code={`# {テストケース名} E2Eテスト結果

## 実施日時

{yyyy-MM-dd HH:mm:ss}

## 結果サマリ

| No | ケース名 | 結果 | 備考 |
|----|----------|------|------|
| 1  | ... | OK | |
| 2  | ... | OK | |
| 3  | ... | NG | レスポンスの○○が期待値と異なる |

## ケース詳細

（各ケースの実行結果詳細）`}
          />
        </WorkflowSection>
      </WorkflowSection>

      {/* セクション6 */}
      <WorkflowSection title="バグ検知時の対応" id="section-6" number={6}>
        <WorkflowSection title="E2Eテスト固有のルール" subNumber="6.1">
          <WorkflowInfoBox type="important">
            <Typography variant="body2" fontWeight={600}>
              E2Eテストでは実装コードの修正を行いません。
            </Typography>
            <WorkflowList
              items={[
                'テストケースの期待値が誤り → AIが .e2e.md を修正して再実行',
                '実装のバグを検知 → result ファイルにNG記録のみ。実装修正は行わない',
                '設計のバグを検知 → result ファイルにNG記録のみ。設計・実装修正は行わない',
              ]}
            />
          </WorkflowInfoBox>
        </WorkflowSection>

        <WorkflowSection title="なぜ実装を修正しないのか" subNumber="6.2">
          <WorkflowTipBox title="E2Eテストで実装を修正しない理由">
            <WorkflowText>
              「実装編 —
              API実装ワークフロー」の単体テストではAIが自動でコードを修正して再テストします。しかしE2Eテストで検知されるバグは、複数APIの組み合わせで初めて顕在化する問題であるケースが多く、修正の影響範囲が広いです。
            </WorkflowText>

            <WorkflowLinkCard
              title="実装編 — API実装ワークフロー"
              description="単体テストでの自動修正フロー"
              path="/devdocs/handson/api-workflow"
            />

            <WorkflowText>
              例: 「TODO更新で担当者IDを変更した後、一覧取得で旧担当者のデータが残っている」
            </WorkflowText>
            <WorkflowText>このバグの原因は:</WorkflowText>
            <WorkflowList
              items={[
                '更新APIのSQLが担当者テーブルを更新していない？（実装バグ）',
                '一覧取得APIのJOIN条件が間違っている？（実装バグ）',
                'スキーマ定義の外部キー設計が不適切？（設計バグ）',
              ]}
            />
            <WorkflowText>
              原因の切り分けと修正方針の判断は人間がやるべきです。AIに自動修正させると、場当たり的な修正で別の箇所を壊すリスクがあります。
            </WorkflowText>
            <WorkflowText>対応フロー:</WorkflowText>
            <WorkflowList
              variant="ordered"
              items={[
                'E2Eテスト実行 → NGが発生',
                'AIがNG原因を判断する',
                'テストケース誤りなら AIが .e2e.md を修正して再実行する',
                '実装バグ・設計バグなら結果をNGとして記録し、実装修正は行わない',
                '人間が結果をレビューして修正方針を判断する',
              ]}
            />
          </WorkflowTipBox>
        </WorkflowSection>

        <WorkflowSection title="対応フロー" subNumber="6.3">
          <WorkflowTable
            headers={['NG原因', '対処']}
            rows={[
              ['テストケースの期待値誤り', 'AIが .e2e.md を修正 → 再実行'],
              ['実装のバグ', 'result に NG 記録（実装修正なし）'],
              [
                '設計のバグ',
                'result に NG 記録 → 人間がレビュー → 原因特定 → 修正方針の判断 → 別途対応 → E2Eテスト再実行',
              ],
            ]}
          />
        </WorkflowSection>
      </WorkflowSection>

      {/* セクション7 */}
      <WorkflowSection title="現在のプロジェクト構成" id="section-7" number={7}>
        <WorkflowDirectoryTree
          root="team-todo/"
          entries={[
            {
              name: '.kiro',
              color: '#1565c0',
              children: [
                { name: 'requirements', children: [{ name: '...（省略）' }] },
                {
                  name: 'steering',
                  children: [
                    { name: 'product.md' },
                    { name: 'structure.md' },
                    { name: 'tech.md' },
                    { name: 'create_schema.md' },
                    { name: 'api_specs_rule.md' },
                    { name: 'api_dev_workflow.md' },
                    { name: 'create_e2e_testcase.md', desc: '今回作成', highlight: '#e65100' },
                  ],
                },
                {
                  name: 'specs',
                  children: [{ name: 'API001/' }, { name: 'API002/' }, { name: '...' }],
                },
                {
                  name: 'e2e_test',
                  desc: '今回生成',
                  highlight: '#1976d2',
                  children: [
                    {
                      name: 'todo',
                      children: [{ name: 'todo.e2e.md' }, { name: 'result_{yyyyMMddHHmmss}.md' }],
                    },
                  ],
                },
              ],
            },
            {
              name: 'docs',
              color: '#2e7d32',
              children: [{ name: 'schema/' }, { name: 'api/' }, { name: 'dd/' }],
            },
            {
              name: 'src',
              color: '#e65100',
              children: [
                {
                  name: 'main/java/com/example/teamtodo',
                  desc: '全APIのコード',
                  children: [],
                },
                {
                  name: 'main/resources',
                  children: [{ name: 'application.yml' }, { name: 'db/migration/' }],
                },
              ],
            },
            { name: 'build.gradle' },
            { name: 'settings.gradle' },
            { name: 'docker-compose.yml' },
            { name: '...' },
          ]}
        />
      </WorkflowSection>

      {/* セクション8 */}
      <WorkflowSection title="ベストプラクティスとアンチパターン" id="section-8" number={8}>
        <WorkflowPracticeBox
          type="best"
          items={[
            {
              practice: '単体テスト全件OK後にE2Eを実施する',
              effect: '個別APIのバグがE2Eシナリオを壊し、問題の切り分けが困難になる',
            },
            {
              practice: 'usecases.md の全UCカバレッジを確認する',
              effect: 'テストケース生成後に「カバーするユースケース」テーブルで漏れを確認する',
            },
            {
              practice: 'テストケース期待値をAPI仕様書と突合する',
              effect: '詳細設計レベルの仕様は自動生成で漏れやすいため手動で補完する',
            },
            {
              practice: 'バグ検知時はresultに記録し、実装修正は別途判断する',
              effect: 'E2Eで検知されるバグは影響範囲が広い。人間が方針を判断してから修正する',
            },
            {
              practice: '作成した .e2e.md をリグレッション試験として再利用する',
              effect: '修正後に再実行して副作用がないことを確認する',
            },
            {
              practice: '期待値の修正とバグの修正を区別する',
              effect: 'テストケース側の不備で直るものと、実装を直す必要があるものを明確に分ける',
            },
          ]}
        />

        <WorkflowPracticeBox
          type="anti"
          items={[
            {
              practice: '単体テスト未完了のAPIをE2Eに含める',
              effect:
                '個別のバグがシナリオ全体を壊し、原因特定が困難。全APIの単体テストOKを前提とする',
            },
            {
              practice: 'E2Eテストで実装を自動修正する',
              effect:
                '影響範囲を考慮しない修正で他の業務フローを壊す。resultに記録し、人間が判断してから修正する',
            },
            {
              practice: '単体テストの観点をE2Eに含める',
              effect:
                'バリデーション網羅や個別エラー確認でケース数が肥大化する。E2Eは業務フロー検証に特化する',
            },
            {
              practice: 'テストケースの期待値誤りと実装バグを区別しない',
              effect:
                'テストケースの不備でコード修正に着手し、不要な変更が入る。まず期待値がAPI仕様と合っているか確認する',
            },
            {
              practice: 'ケース間のデータ依存を無視して順序を変える',
              effect:
                '前ケースのデータが存在せず後続ケースが全て失敗する。実行順序を守り、前提条件の依存を意識する',
            },
            {
              practice: 'E2Eテストをスキップして完了とする',
              effect: '複数API連携のバグが本番で発覚する。業務シナリオで一連のフローを必ず検証する',
            },
          ]}
        />
      </WorkflowSection>

      {/* セクション9 */}
      <WorkflowSection title="この章で学んだこと" id="section-9" number={9}>
        <WorkflowTable
          headers={['学び', '内容']}
          rows={[
            ['単体テストとE2Eは補完関係', '単体=各APIの品質保証、E2E=業務フローの整合性確認'],
            ['E2Eでは実装を修正しない', '影響範囲が広いバグは人間が判断してから対応する'],
            ['テストケースはusecases.mdベース', '要件定義の成果物がテスト設計の入力になる'],
            ['.e2e.md はリグレッション資産', '一度作ればいつでも再実行できる品質保証の仕組み'],
          ]}
        />
      </WorkflowSection>

      {/* セクション10 */}
      <WorkflowSection title="バックエンド実装編の全体まとめ" id="section-10" number={10}>
        <WorkflowInfoBox type="success">
          <Typography variant="body2" fontWeight={600}>
            おめでとうございます！ バックエンド実装編が全て完了しました。
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            「実装編 —
            E2Eテスト」が全件OKで完了すると、バックエンド実装の全工程が完了です。ここまでの内容を、ストーリーの相談相手であるAさんに「動くもの」として見せられる状態です。要件定義から実装・テストまでをAI駆動で一気通貫で実現した体験は、今後のプロジェクトでも活きるはずです。
          </Typography>
        </WorkflowInfoBox>

        <WorkflowSection title="実装編で作成したもの" subNumber="10.1">
          <WorkflowTable
            headers={['記事', '作成したSteering', '生成した成果物']}
            rows={[
              ['structure/techルールの作成', 'structure.md, tech.md', '—'],
              [
                'スキーマ定義 + API仕様書の生成',
                'create_schema.md, api_specs_rule.md',
                'スキーマ定義書, API仕様書, マイグレーションSQL',
              ],
              ['API実装ワークフロー', 'api_dev_workflow.md', 'DD書, Javaコード, 単体テスト結果'],
              ['E2Eテスト', 'create_e2e_testcase.md', 'E2Eテストケース, テスト結果'],
            ]}
          />
        </WorkflowSection>

        <WorkflowSection title="最終的に得られるもの" subNumber="10.2">
          <WorkflowList
            items={[
              'スキーマ定義書（全テーブル）',
              'API仕様書（全API）',
              'DD書（設計書 — コードと整合済み）',
              'Javaソースコード（ビルド＆テスト通過済み）',
              'Flywayマイグレーション',
              '単体テスト結果（全API × 全ケースOK）',
              'E2Eテスト結果（業務シナリオ全件OK）',
            ]}
          />
        </WorkflowSection>

        <WorkflowSection title="振り返り — ハンズオン全体を通じて学んだこと" subNumber="10.3">
          <WorkflowList
            variant="ordered"
            items={[
              'ルールの品質が出力の品質を決める — Steeringルールが具体的であるほど、AIの生成物が安定する',
              'PDCAサイクルでルールを育てる — 一度で完璧なルールは書けない。生成→チェック→改善の繰り返し',
              '段階的に精度を上げる — rfp → proposal → usecases → スキーマ → API仕様書 → コード の各段階で品質が積み上がる',
              '設計書はAIとの共通言語 — DD書やAPI仕様書があることでAIが正確にコードを生成できる',
              'テストは設計書ベース — 実装から独立した期待値でバグを検出する',
              'ワークフローは育てるもの — APIを重ねるたびに改善し、プロジェクトに最適化される',
            ]}
          />
        </WorkflowSection>
      </WorkflowSection>

      <WorkflowInfoBox type="preview">
        <Typography variant="body2" fontWeight={600}>
          次の記事: フロントエンド編 — プロジェクト作成 + Steeringルール設置
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          次の記事では以下を行います:
        </Typography>
        <WorkflowList
          items={[
            'Vite + React + TypeScriptのプロジェクトを作成する',
            'Steeringルール（product / tech / structure）を設置する',
            'AIによる画面実装の準備を完了する',
          ]}
        />
      </WorkflowInfoBox>

      <WorkflowLinkCard
        title="フロントエンド編 — プロジェクト作成 + Steeringルール設置"
        description="AWS環境へのデプロイに向けて、プロジェクト構成とSteeringルールを整備する"
        path="/devdocs/handson/project-setup"
      />

      <WorkflowChecklist
        groups={[
          {
            title: '最終チェックリスト',
            items: [
              '.kiro/steering/create_e2e_testcase.md が作成されている',
              '.kiro/e2e_test/{テストケース名}/{テストケース名}.e2e.md が生成されている',
              'usecases.md の全ユースケースが少なくとも1つのテストケースでカバーされている',
              'E2Eテストが全ケースOKで、結果が result_{yyyyMMddHHmmss}.md に記録されている',
              'テストケースの期待値がAPI仕様書と整合している',
              'バグ検知時は result に記録し、実装コードを修正していない',
            ],
          },
        ]}
      />
    </HandsonArticleLayout>
  );
};

export default HandsonE2e;
